import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useI18n } from '../../i18n';
import './TutorialsPanel.css';

// Icons - matching the app's icon style (strokeWidth 2, clean minimal)
const VideoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M10 9l5 3-5 3V9z" fill="currentColor" stroke="none" />
  </svg>
);

const ChevronIcon = ({ expanded }) => (
  <svg 
    width="14" 
    height="14" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2"
    style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const PlayIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <polygon points="6 3 20 12 6 21 6 3" />
  </svg>
);

const TourIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

const ExternalIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

// Helper to extract YouTube video ID
const getYouTubeVideoId = (url) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

// Tutorials data
const tutorials = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    preview: 'Upload & begin',
    description: 'Upload your project and start with ArabCap',
    youtubeUrl: 'https://www.youtube.com/watch?v=qdCZlZ9ygkQ',
    duration: '2:33'
  },
  {
    id: 'edit-subtitles',
    title: 'Edit Subtitles',
    preview: 'Adjust words',
    description: 'Adjust and reorganize words in the editor',
    youtubeUrl: 'https://youtu.be/ArZVY8miaUI',
    duration: '3:15'
  },
  {
    id: 'split-chunks',
    title: 'Split & Chunks',
    preview: 'Manage display',
    description: 'Master word separation and display',
    youtubeUrl: 'https://youtu.be/POEzmhUBct8',
    duration: '2:45'
  },
  {
    id: 'convert-words',
    title: 'Convert Words',
    preview: 'Arabic ↔ Latin',
    description: 'Convert between Arabic, Latin, and translations',
    youtubeUrl: 'https://youtu.be/ZFXFn1eWP1Q',
    duration: '3:00'
  },
  {
    id: 'add-capcut',
    title: 'Add Subtitles to Capcut',
    preview: 'Add Subtitles',
    description: 'Add subtitles to your video in Capcut',
    youtubeUrl: 'https://youtu.be/5nOo6StMBHI',
    duration: '1:48'
  },
  {
    id: 'adjust-timing',
    title: 'Adjust the delay of the subtitles',
    preview: 'Adjust delay ',
    description: 'Adjust the delay of the subtitles',
    youtubeUrl: 'https://youtu.be/IJhSsOnyt_M',
    duration: '4:11'
  }
];

// Video Modal
const VideoModal = ({ tutorial, onClose }) => {
  const { t } = useI18n();
  const videoId = getYouTubeVideoId(tutorial.youtubeUrl);
  
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return ReactDOM.createPortal(
    <div className="tutorial-modal-overlay" onClick={onClose}>
      <div className="tutorial-modal" onClick={(e) => e.stopPropagation()}>
        <button className="tutorial-modal-close" onClick={onClose}>
          <XIcon />
        </button>
        <div className="tutorial-modal-video">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
            title={t(tutorial.title)}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <div className="tutorial-modal-info">
          <span className="tutorial-modal-title">{t(tutorial.title)}</span>
          <a 
            href={tutorial.youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="tutorial-modal-link"
          >
            <ExternalIcon />
            YouTube
          </a>
        </div>
      </div>
    </div>,
    document.body
  );
};

const TutorialsPanel = ({ onStartTour }) => {
  const { t } = useI18n();
  const [isExpanded, setIsExpanded] = useState(true);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  const panelRef = useRef(null);

  // Rotate previews
  useEffect(() => {
    if (isExpanded) return;
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentPreviewIndex((prev) => (prev + 1) % tutorials.length);
        setIsAnimating(false);
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, [isExpanded]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeVideo) return;
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsExpanded(false);
      }
    };
    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded, activeVideo]);

  const currentTutorial = tutorials[currentPreviewIndex];

  return (
    <>
      <div ref={panelRef} className={`tutorials-panel ${isExpanded ? 'expanded' : ''}`}>
        {/* Collapsed */}
        {!isExpanded && (
          <div className="tutorials-collapsed-container">
            {/* Tour Button - Always visible */}
            {onStartTour && (
              <button 
                className="tutorials-collapsed-tour"
                onClick={onStartTour}
                title={t('Take a Tour')}
              >
                <PlayIcon />
              </button>
            )}
            {/* Tutorials Button */}
            <button className="tutorials-collapsed" onClick={() => setIsExpanded(true)}>
              <span className="tutorials-icon">
                <VideoIcon />
              </span>
              <span className={`tutorials-preview ${isAnimating ? 'animating' : ''}`}>
                {t(currentTutorial.preview)}
              </span>
              <span className="tutorials-label">{t('Tutorials')}</span>
            </button>
          </div>
        )}

        {/* Expanded */}
        {isExpanded && (
          <div className="tutorials-expanded">
            <div className="tutorials-header">
              <div className="tutorials-header-left">
                <span className="tutorials-header-icon">
                  <VideoIcon />
                </span>
                <span>{t('Tutorials')}</span>
              </div>
              <button className="tutorials-close" onClick={() => setIsExpanded(false)}>
                <XIcon />
              </button>
            </div>

            {/* Interactive Tour Button */}
            {onStartTour && (
              <button 
                className="tutorial-tour-btn"
                onClick={() => {
                  setIsExpanded(false);
                  onStartTour();
                }}
              >
                <span className="tutorial-tour-icon">
                  <TourIcon />
                </span>
                <div className="tutorial-tour-text">
                  <span className="tutorial-tour-title">{t('Take an Interactive Tour')}</span>
                  <span className="tutorial-tour-desc">{t('Learn the editor step by step')}</span>
                </div>
                <span className="tutorial-tour-badge">{t('Quick')}</span>
              </button>
            )}

            <div className="tutorials-list">
              {tutorials.map((tutorial) => {
                const videoId = getYouTubeVideoId(tutorial.youtubeUrl);
                const thumbnail = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
                
                return (
                  <button 
                    key={tutorial.id}
                    className="tutorial-card"
                    onClick={() => setActiveVideo(tutorial)}
                  >
                    <div className="tutorial-thumb">
                      <img src={thumbnail} alt="" />
                      <div className="tutorial-thumb-play">
                        <PlayIcon />
                      </div>
                      <span className="tutorial-duration">{tutorial.duration}</span>
                    </div>
                    <div className="tutorial-info">
                      <span className="tutorial-title">{t(tutorial.title)}</span>
                      <span className="tutorial-desc">{t(tutorial.description)}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="tutorials-footer">
              {t('More coming soon')}
            </div>
          </div>
        )}
      </div>

      {activeVideo && (
        <VideoModal tutorial={activeVideo} onClose={() => setActiveVideo(null)} />
      )}
    </>
  );
};

export default TutorialsPanel;
